import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useGetList } from "react-admin";
import {
  UserIcon,
  PhoneIcon,
  Calendar,
  Loader2Icon,
  User,
  AppWindowIcon,
  MapPin,
  Receipt,
  Pen,
} from "lucide-react";
import axios from "axios";
import { BASE_URL } from "@/config";
import { Badge } from "@/components/ui/badge";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { useGoogleMaps } from "@/context/GoogleMapsContext";
import OrderTimeline from "./OrderTimeline";
import { Slider } from "@/components/ui/slider";
import { addHours, format } from "date-fns";
import { grayMapStyle, meters_to_km_velocity } from "@/utils/variables";
import { Orders } from "@/interface/entityInterface";
import { getFormattedDate } from "@/utils/generateTimeSlots";
import { Button } from "@/components/ui/button";
import OrderDetailEdit from "./OrderDetailEdit";
import { taxiStatusItems } from "@/constants/status";

interface Props {
  selectedOrder: Orders | null;
  isSheetOpen: boolean;
  setIsSheetOpen: Dispatch<SetStateAction<boolean>>;
}

interface Position {
  latitude: number;
  longitude: number;
  altitude?: number;
}

interface Variables {
  ignition: boolean;
  batteryVoltage?: number;
  customBoolean2?: boolean;
  gsmSignalLevel?: number;
  hdop?: number;
  satelliteCount?: number;
  speed?: number;
  voltage?: number;
}

interface Record {
  position: Position;
  serverUtc: string;
  trackInfoId: number;
  utc: string;
  valid: boolean;
  variables: Variables;
  velocity?: { groundSpeed: number; heading: number };
}

interface Stop {
  startTime: string;
  startCoords: Position;
  endTime?: string;
  endCoords?: Position;
  durationSeconds?: number;
}

const OrderDetails: FC<Props> = ({
  selectedOrder,
  isSheetOpen,
  setIsSheetOpen,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);

  const [stops, setStops] = React.useState<any>([]);

  const [loadingPolylines, setLoadingPolylines] = React.useState(false);
  const [polylines, setPolylines] = React.useState<any>([]);

  const [currentPosition, setCurrentPosition] = React.useState<any>(null);
  const [sliderValue, setSliderValue] = React.useState(0);
  const [editOpen, setOpen] = useState<boolean>(false);

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const { isLoaded, loadError } = useGoogleMaps();
  const status = useMemo(() => taxiStatusItems.find(item => item.status === selectedOrder?.status), [selectedOrder?.id])

  const { data: vehicles } = useGetList("gps-track/track-vehicles-status", {
    pagination: { page: 1, perPage: 23 },
    sort: { field: "date", order: "DESC" },
    filter: {},
  });

  const getGpsVehicleDetailsId = async (): Promise<string | undefined> => {
    if (!vehicles || !selectedOrder?.vehicle_id) return;

    const match = vehicles.find((v: any) => v.id === selectedOrder.vehicle_id);
    return match?.gpsData?.id;
  };

  const calculateStops = (
    data: Record[],
    minStopDuration: number = 60,
  ): Stop[] => {
    const stops: Stop[] = [];
    let currentStop: Stop | null = null;
    let previousIgnition = true;

    for (const record of data) {
      const ignition = record.variables.ignition;

      if (!ignition && previousIgnition) {
        currentStop = {
          startTime: record.utc,
          startCoords: record.position,
        };
      }

      if (ignition && !previousIgnition && currentStop) {
        currentStop.endTime = record.utc;
        currentStop.endCoords = record.position;

        const startTime = new Date(currentStop.startTime);
        const endTime = new Date(currentStop.endTime);
        currentStop.durationSeconds =
          (endTime.getTime() - startTime.getTime()) / 1000;

        if (currentStop.durationSeconds >= minStopDuration) {
          stops.push(currentStop);
        }

        currentStop = null;
      }

      previousIgnition = ignition;
    }

    if (currentStop) {
      currentStop.endTime = data[data.length - 1].utc;
      currentStop.endCoords = data[data.length - 1].position;

      const startTime = new Date(currentStop.startTime);
      const endTime = new Date(currentStop.endTime);
      currentStop.durationSeconds =
        (endTime.getTime() - startTime.getTime()) / 1000;

      if (currentStop.durationSeconds >= minStopDuration) {
        stops.push(currentStop);
      }
    }

    return stops;
  };

  const calculateRoute = async () => {
    if (!selectedOrder) return;
    const formattedDate = format(
      new Date(selectedOrder?.pickup_time),
      "yyyy-MM-dd",
    );
    const startTime = format(
      addHours(new Date(selectedOrder?.pickup_time), 0),
      "HH:mm:ss",
    );
    const endTime = format(
      addHours(new Date(selectedOrder?.dropoff_time), 0),
      "HH:mm:ss",
    );
    try {
      const gpsid = await getGpsVehicleDetailsId();

      const url = `${BASE_URL}/gps-track/users/${gpsid}/tracks/${formattedDate}?startTime=${startTime}&endTime=${endTime}&filtered=true`;

      setLoadingPolylines(true);

      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const routeData = response.data;

      const filteredPolylines = routeData.filter(
        (el: any) => el.velocity?.groundSpeed > 0,
      );

      setPolylines(filteredPolylines);

      const stops = calculateStops(response.data);
      setStops(stops);
    } catch (error) {
      console.error("Error fetching route data:", error);
    } finally {
      setLoadingPolylines(false);
    }
  };

  React.useEffect(() => {
    if (polylines.length > 0) {
      const index = Math.floor((sliderValue / 100) * (polylines.length - 1));
      setCurrentPosition(polylines[index]);
    }
  }, [sliderValue, polylines]);

  const handleSliderChange = (value: number[]) => {
    setSliderValue(value as unknown as number);
  };

  useEffect(() => {
    if (isSheetOpen) {
      calculateRoute();
      return () => {
        setPolylines([]);
      };
    }
  }, [isSheetOpen]);

  useEffect(() => {
    if (mapRef.current && polylines.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      polylines.forEach((point: any) => {
        bounds.extend({
          lat: point.position.latitude,
          lng: point.position.longitude,
        });
      });

      mapRef.current.fitBounds(bounds);
    }
  }, [polylines]);

  useEffect(() => {
  }, [selectedOrder?.id])

  if (loadError) return <p>Error loading Google Maps: {loadError.message}</p>;
  if (!isLoaded) return <p>Loading map...</p>;
  if (!selectedOrder) return;

  return (
    <>
      <Sheet open={editOpen ? false : isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent
          side="bottom"
          className="p-5 max-h-[85vh] overflow-y-auto"
        >
          <SheetTitle className="hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6 lg:gap-8">
            <div className="!rounded-xl w-full">
              <GoogleMap
                zoom={14}
                center={
                  selectedOrder?.pickup_latitude
                    ? {
                        lat: selectedOrder?.pickup_latitude,
                        lng: selectedOrder?.pickup_longitude,
                      }
                    : { lat: 41.3275, lng: 19.8189 }
                }
                mapContainerClassName="w-full h-full rounded-xl"
                onLoad={onMapLoad}
                options={{
                  styles: grayMapStyle,
                  disableDefaultUI: true,
                  zoomControl: true,
                  gestureHandling: "greedy",
                }}
              >
                {selectedOrder?.pickup && selectedOrder.dropoff && (
                  <>
                    <>
                      <Marker
                        label="S"
                        position={{
                          lat: selectedOrder?.pickup_latitude,
                          lng: selectedOrder?.pickup_longitude,
                        }}
                      />
                      <Marker
                        label="E"
                        position={{
                          lat: selectedOrder.dropoff_latitude,
                          lng: selectedOrder.dropoff_longitude,
                        }}
                      />
                    </>
                    {currentPosition && (
                      <Marker
                        position={{
                          lat: currentPosition?.position.latitude,
                          lng: currentPosition?.position.longitude,
                        }}
                      />
                    )}
                    {polylines?.length >= 2 && (
                      <Polyline
                        path={polylines.map((point: any) => ({
                          lat: point.position.latitude,
                          lng: point.position.longitude,
                        }))}
                        options={{
                          strokeColor: "#0000FF",
                          strokeOpacity: 0.8,
                          strokeWeight: 4,
                        }}
                      />
                    )}
                    {stops &&
                      stops?.length > 0 &&
                      stops?.map((item: any, index: number) => (
                        <Marker
                          key={index}
                          position={{
                            lat: item.startCoords.latitude,
                            lng: item.startCoords.longitude,
                          }}
                        />
                      ))}
                    {loadingPolylines && (
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] bg-white/80 p-4 rounded-2xl">
                        <Loader2Icon className="animate-spin w-6 h-6 text-gray-800" />
                      </div>
                    )}
                    <div className="absolute bottom-10 left-10 bg-white p-5 w-120 rounded-2xl font-bold">
                      <p className="pb-3">Track Route</p>
                      <Slider
                        defaultValue={currentPosition ? [currentPosition] : [0]}
                        onValueChange={handleSliderChange}
                        max={100}
                        step={1}
                      />
                      <div className="relative pt-4 text-sm text-gray-700">
                        {currentPosition && (
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <User
                                className="w-5 h-5"
                                style={{
                                  color:
                                    currentPosition?.variables.seatSensor ||
                                    currentPosition?.variables.pasagjer
                                      ? "#ff4d4d"
                                      : "#00ff00",
                                }}
                              />
                              <span className="text-xs">Pasager</span>
                            </div>

                            <div className="flex items-center space-x-1">
                              <AppWindowIcon
                                className="w-5 h-5"
                                style={{
                                  color: currentPosition?.variables.taxiMeter
                                    ? "#ff4d4d"
                                    : "#00ff00",
                                }}
                              />
                              <span className="text-xs">Taximeter</span>
                            </div>

                            {currentPosition?.variables?.speed != null && (
                              <div className="ml-auto text-xs text-gray-600">
                                <span className="font-medium">Speed:</span>{" "}
                                {Math.round(
                                  currentPosition.variables.speed *
                                    meters_to_km_velocity,
                                )}{" "}
                                km/h
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {selectedOrder?.status != "COMPLETED" && (
                  <Marker
                    position={{
                      lat: selectedOrder?.pickup_latitude || 41.3275,
                      lng: selectedOrder?.pickup_longitude || 19.8189,
                    }}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 10,
                      fillColor: "#222",
                      fillOpacity: 1,
                      strokeWeight: 1,
                      strokeColor: "#ffffff",
                    }}
                  />
                )}
                {stops &&
                  stops?.length > 0 &&
                  stops?.map((item: any, index: number) => (
                    <Marker
                      key={index}
                      position={{
                        lat: item.startCoords.latitude,
                        lng: item.startCoords.longitude,
                      }}
                    />
                  ))}
                {loadingPolylines && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] bg-white/80 p-4 rounded-2xl">
                    <Loader2Icon className="animate-spin w-6 h-6 text-gray-800" />
                  </div>
                )}
                <div className="absolute bottom-10 left-10 bg-white p-5 w-120 rounded-2xl font-bold">
                  <p className="pb-3">Track Route</p>
                  {currentPosition && (
                    <Slider
                      defaultValue={[currentPosition]}
                      onValueChange={handleSliderChange}
                      max={100}
                      step={1}
                    />
                  )}
                  {currentPosition && (
                    <div className="pt-4 space-y-1 text-sm text-gray-700">
                      <p>
                        Speed:{" "}
                        {Math.round(
                          currentPosition?.variables.speed *
                            meters_to_km_velocity,
                        )}{" "}
                        km/h
                      </p>
                      <p>
                        Seat Sensor:{" "}
                        {currentPosition?.variables.pasagjer
                          ? "Occupied"
                          : "Empty"}
                      </p>
                      <p>
                        Taximeter:{" "}
                        {currentPosition?.variables.taximeter ? "ON" : "OFF"}
                      </p>
                    </div>
                  )}
                </div>
              </GoogleMap>
            </div>
            <div>
              <div>
                <div>
                  <p className="text-lg sm:text-4xl font-bold">
                    Order #{selectedOrder.id}
                  </p>
                  <p>
                    {selectedOrder?.created_at
                      ? getFormattedDate(selectedOrder.created_at)
                      : "-"}
                  </p>
                </div>
                {selectedOrder?.flight_number && (
                  <p className="text-md font-semibold mt-2">
                    Flight Number: {selectedOrder.flight_number}
                  </p>
                )}
              </div>
              <div className="flex gap-2 mt-5">
                <Badge
                  variant="outline"
                  className={`flex gap-1 px-1.5 [&_svg]:size-3 ${status?.color || "text-muted-foreground"}`}
                >
                  {status?.icon ? status?.icon.render("") : ""}
                  {status?.label}
                </Badge>
              </div>
              <div>
                <div className="flex items-center gap-x-2">
                  <h2 className="text-lg font-semibold mb-4 mt-5">
                    Order Info
                  </h2>
                  <Button
                    variant="outline"
                    className="w-6 h-6 p-1"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpen(true);
                    }}
                  >
                    <Pen className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <Calendar className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Pickup date
                    </p>
                    <p className="text-[14px] font-medium">
                      {selectedOrder?.pickup_time
                        ? getFormattedDate(selectedOrder?.pickup_time)
                        : "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <MapPin className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Pickup
                    </p>
                    <p className="text-[14px] font-medium line-clamp-3">
                      {selectedOrder?.pickup || "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <MapPin className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Dropoff
                    </p>
                    <p className="text-[14px] font-medium break-words overflow-hidden line-clamp-3">
                      {selectedOrder?.dropoff || "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <UserIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Client Name
                    </p>
                    <p className="text-[14px] font-medium">
                      {selectedOrder?.client_name || "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <PhoneIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Client Phone
                    </p>
                    <p className="text-[14px] font-medium">
                      {selectedOrder?.client_phone || "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <Receipt className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Order price
                    </p>
                    <p className="text-[14px] font-medium">
                      {selectedOrder?.fare ? `${selectedOrder.fare} ${selectedOrder.currency || "ALL"}` : "-"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5">
                <p className="text-lg font-semibold mt-5">Notes</p>
                <p>{selectedOrder?.notes ? selectedOrder?.notes : "-"}</p>
              </div>
            </div>
            <div>
              <OrderTimeline id={selectedOrder?.id} />
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <OrderDetailEdit
        isOpen={editOpen}
        setOpen={setOpen}
        selectedOrder={selectedOrder}
      />
    </>
  );
};

export default OrderDetails;
